import * as React from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { trackCtaClick, trackCtaImpression } from "@/lib/observability";
import {
  CTA_LIBRARY,
  type CtaHierarchy,
  type CtaIntent,
  type CtaDefinition,
} from "@/lib/ctaLibrary";
import { getVariant, trackExposure } from "@/lib/experiment";
import { useImpression } from "@/lib/useImpression";

/**
 * Reusable CTA button.
 *
 * Enforces the style guide's wording (§7), hierarchy, and a11y rules:
 *  - Label is pulled from ctaLibrary — cannot be overridden with free text.
 *  - Hierarchy (primary / secondary / tertiary) maps to a consistent visual
 *    weight, so a page only ever shows one primary action per group.
 *  - Minimum 44×44 tap target (min-h-11 min-w-11) for mobile a11y.
 *  - Loading state uses the guide-approved present-continuous label and
 *    keeps the button an accessible name via aria-label.
 *  - trackCtaClick fires automatically with the intent's analytics id.
 */

const HIERARCHY_VARIANT: Record<CtaHierarchy, ButtonProps["variant"]> = {
  primary: "premium",
  secondary: "outline",
  tertiary: "ghost",
};

export interface CtaButtonProps
  extends Omit<ButtonProps, "children"> {
  intent: CtaIntent;
  /** Override the library's default hierarchy for this instance. */
  hierarchy?: CtaHierarchy;
  /** Optional analytics surface (e.g. "hero", "navbar_desktop"). */
  source?: string;
  /** Extra metadata forwarded to trackCtaClick. */
  analyticsMetadata?: Record<string, unknown>;
  /** Show the trailing arrow. Defaults to true for primary actions. */
  showArrow?: boolean;
  loading?: boolean;
  /** Renders <a>/<Link>-shaped children via asChild while keeping label. */
  asChild?: boolean;
  /**
   * Opt-in A/B test for this CTA's label. When set, the button:
   *  - resolves a sticky variant via getVariant(experiment.key, variants)
   *  - swaps the visible label to variants[variant] (must be a PCM+NLP-safe
   *    string registered in CTA_VARIANT_COPY)
   *  - fires trackExposure once per page load
   *  - attaches `experiments: { [key]: variant }` to trackCtaClick metadata
   */
  experiment?: {
    key: string;
    variants: Readonly<Record<string, string>>;
  };
  /** Reserved — labels always come from the library or a registered experiment. */
  labelOverride?: never;
}


const CtaButton = React.forwardRef<HTMLButtonElement, CtaButtonProps>(
  (
    {
      intent,
      hierarchy,
      source,
      analyticsMetadata,
      showArrow,
      loading = false,
      onClick,
      className,
      size = "lg",
      variant,
      disabled,
      type = "button",
      experiment,
      "aria-label": ariaLabelProp,
      ...rest
    },
    ref,
  ) => {
    const def: CtaDefinition = CTA_LIBRARY[intent];
    const resolvedHierarchy = hierarchy ?? def.defaultHierarchy;
    const resolvedVariant = variant ?? HIERARCHY_VARIANT[resolvedHierarchy];
    const showTrailingArrow =
      showArrow ?? (resolvedHierarchy !== "tertiary");

    // Sticky A/B assignment for the label copy, if an experiment is provided.
    const variantKeys = React.useMemo(
      () => (experiment ? (Object.keys(experiment.variants) as string[]) : []),
      [experiment],
    );
    const abVariant = experiment && variantKeys.length > 0
      ? getVariant(experiment.key, variantKeys as readonly string[])
      : null;
    React.useEffect(() => {
      if (experiment && abVariant) trackExposure(experiment.key, abVariant);
    }, [experiment, abVariant]);

    const experimentLabel =
      experiment && abVariant ? experiment.variants[abVariant] : undefined;
    const baseLabel = experimentLabel ?? def.label;
    const label = loading ? def.loadingLabel ?? baseLabel : baseLabel;

    // Sticky experiments map for BOTH impression and click events.
    const experimentsMeta = React.useMemo<Record<string, string> | undefined>(() => {
      if (experiment && abVariant) return { [experiment.key]: abVariant };
      return undefined;
    }, [experiment, abVariant]);

    // Fire a viewport-based impression event once the button is actually seen.
    // Distinct from experiment_exposure (which fires on mount).
    const { ref: impressionRef, firstVisibleAtRef } = useImpression<HTMLButtonElement>(
      () => {
        if (!source) return;
        trackCtaImpression(source, def.analyticsId, {
          experiments: experimentsMeta,
          variant_label: experimentsMeta ? baseLabel : undefined,
        });
      },
      { disabled: loading || !source },
    );

    // Compose the forwarded ref with the impression observer's ref callback.
    const composedRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        impressionRef(node);
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      },
      [impressionRef, ref],
    );

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (source) {
          const meta = { ...(analyticsMetadata ?? {}) } as Record<string, unknown>;
          if (experimentsMeta) {
            const prev = (meta.experiments as Record<string, string> | undefined) ?? {};
            meta.experiments = { ...prev, ...experimentsMeta };
            meta.variant_label = baseLabel;
          }
          // Time-to-click: ms between first qualifying impression and click.
          if (firstVisibleAtRef.current != null) {
            meta.ms_to_click = Date.now() - firstVisibleAtRef.current;
          }
          trackCtaClick(source, def.analyticsId, meta);
        }
        onClick?.(event);
      },
      [analyticsMetadata, def.analyticsId, onClick, source, experimentsMeta, baseLabel, firstVisibleAtRef],
    );


    return (
      <Button
        ref={composedRef}
        type={type}
        variant={resolvedVariant}
        size={size}
        disabled={disabled || loading}
        aria-label={ariaLabelProp ?? def.ariaLabel ?? baseLabel}
        aria-busy={loading || undefined}
        onClick={handleClick}
        className={cn(
          // 44×44 minimum tap target regardless of size prop
          "min-h-11 min-w-11 group",
          className,
        )}
        {...rest}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" />
        ) : null}
        <span>{label}</span>
        {showTrailingArrow && !loading ? (
          <ArrowRight
            className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1"
            aria-hidden="true"
          />
        ) : null}
      </Button>
    );
  },
);
CtaButton.displayName = "CtaButton";

export { CtaButton };
